import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Justiniano } from "@tribunalea/justiniano";
import "./extension-widget.css";

const CONTEXT_MESSAGE = "tribunalea-extension:context";
const READY_MESSAGE = "tribunalea-extension:ready";
const CLOSE_MESSAGE = "tribunalea-extension:close";
const TOKEN_REQUEST_MESSAGE = "tribunalea-extension:auth-token-request";
const TOKEN_RESPONSE_MESSAGE = "tribunalea-extension:auth-token-response";
const OPEN_AUTH_SETUP_MESSAGE = "tribunalea-extension:open-auth-setup";
const MAX_PAGE_TEXT_LENGTH = 40_000;
const TOKEN_REQUEST_TIMEOUT = 10_000;

function extensionChannel() {
  const value = new URLSearchParams(window.location.search).get(
    "extensionChannel",
  );
  return /^[a-f0-9]{36}$/u.test(value || "") ? value : null;
}

function integrationWindow() {
  if (window.opener && !window.opener.closed) return window.opener;
  return window.parent !== window ? window.parent : null;
}

function isAllowedParentOrigin(origin) {
  try {
    const url = new URL(origin);
    return (
      (import.meta.env.DEV && ["localhost", "127.0.0.1"].includes(url.hostname))
      ||
      url.protocol === "chrome-extension:"
      || url.origin === "https://wwwnoti.jus.mendoza.gov.ar"
      || url.origin === "https://iol.jus.mendoza.gov.ar"
    );
  } catch {
    return false;
  }
}

function cleanString(value, maximum = 1_000) {
  return String(value || "").trim().slice(0, maximum);
}

function cleanExpandedExpedientes(value) {
  if (!Array.isArray(value)) return [];
  return value.slice(0, 100).map((expediente, index) => ({
    position: Number.isInteger(expediente?.position)
      ? expediente.position
      : index + 1,
    cuij: cleanString(expediente?.cuij, 32) || null,
    title: cleanString(expediente?.title, 500) || "Expediente de IOL",
  }));
}

function cleanContext(value) {
  if (!value || typeof value !== "object") return null;
  const text = cleanString(value.text, MAX_PAGE_TEXT_LENGTH);
  const url = cleanString(value.url, 2_000);
  if (!text && !url) return null;
  const expandedExpedientes = cleanExpandedExpedientes(
    value.expandedExpedientes,
  );
  return {
    source: "mendoza_judiciary_extension",
    kind: cleanString(value.kind, 64) || "external_page",
    title: cleanString(value.title, 500) || "Poder Judicial de Mendoza",
    url,
    text,
    cuij: cleanString(value.cuij, 32) || null,
    expandedCount: expandedExpedientes.length,
    expandedExpedientes,
    expId: cleanString(value.expId, 300) || null,
    actId: cleanString(value.actId, 100) || null,
    capturedAt: cleanString(value.capturedAt, 64) || new Date().toISOString(),
  };
}

export function ExtensionWidgetSignIn({
  authError,
  checking = false,
  onConfigure,
  onRetry,
}) {
  const needsPermission = authError === "permissions_required";
  return (
    <main className="extension-widget-sign-in">
      <img src="/favicon.svg" alt="" />
      <span>TRIBUNALEA</span>
      <h1>
        {needsPermission
          ? "Conectá la extensión con Praxis"
          : "Iniciá sesión para usar Justiniano"}
      </h1>
      <p>
        {needsPermission
          ? "Chrome necesita tu permiso una sola vez para leer la sesión de Praxis."
          : "Abrí Praxis en una pestaña, iniciá sesión y después sincronizá este panel."}
      </p>
      {needsPermission ? (
        <button
          className="extension-widget-primary-action"
          type="button"
          onClick={onConfigure}
        >
          Configurar acceso
        </button>
      ) : (
        <a href="/" target="_blank" rel="noreferrer">
          Abrir Praxis
        </a>
      )}
      <button type="button" disabled={checking} onClick={onRetry}>
        {checking
          ? "Verificando…"
          : needsPermission
            ? "Ya concedí acceso"
            : "Ya inicié sesión"}
      </button>
    </main>
  );
}

export default function ExtensionWidgetPage({ getAccessToken }) {
  const [pageContext, setPageContext] = useState(null);
  const [authState, setAuthState] = useState("checking");
  const [authError, setAuthError] = useState(null);
  const parentOrigin = useRef("*");
  const channel = useRef(extensionChannel());
  const targetWindow = useRef(integrationWindow());
  const pendingTokenRequests = useRef(new Map());

  const requestExtensionToken = useCallback(() => new Promise((resolve) => {
    const target = targetWindow.current;
    if (!target || !channel.current || parentOrigin.current === "*") {
      resolve({ token: null, error: "auth_unavailable" });
      return;
    }
    const requestId = crypto.randomUUID();
    const timer = window.setTimeout(() => {
      pendingTokenRequests.current.delete(requestId);
      resolve({ token: null, error: "request_timeout" });
    }, TOKEN_REQUEST_TIMEOUT);
    pendingTokenRequests.current.set(requestId, { resolve, timer });
    target.postMessage({
      type: TOKEN_REQUEST_MESSAGE,
      channel: channel.current,
      requestId,
    }, parentOrigin.current);
  }), []);

  const widgetAccessToken = useCallback(async () => {
    const result = getAccessToken
      ? { token: await getAccessToken(), error: null }
      : await requestExtensionToken();
    const token = result?.token || null;
    setAuthError(token ? null : result?.error || "signed_out");
    setAuthState(token ? "ready" : "signed_out");
    return token;
  }, [getAccessToken, requestExtensionToken]);

  const checkAuthentication = useCallback(async () => {
    setAuthState("checking");
    await widgetAccessToken();
  }, [widgetAccessToken]);

  useEffect(() => {
    const target = targetWindow.current;
    if (!target) return undefined;
    const receive = (event) => {
      if (event.source !== target
        || event.data?.channel !== channel.current
        || !isAllowedParentOrigin(event.origin)) {
        return;
      }
      parentOrigin.current = event.origin;
      if (event.data?.type === CONTEXT_MESSAGE) {
        setPageContext(cleanContext(event.data.context));
      } else if (event.data?.type === TOKEN_RESPONSE_MESSAGE) {
        const pending = pendingTokenRequests.current.get(event.data.requestId);
        if (!pending) return;
        window.clearTimeout(pending.timer);
        pendingTokenRequests.current.delete(event.data.requestId);
        pending.resolve({
          token: cleanString(event.data.token, 8_000) || null,
          error: cleanString(event.data.error, 100) || null,
        });
      }
    };
    window.addEventListener("message", receive);
    target.postMessage({
      type: READY_MESSAGE,
      channel: channel.current,
    }, "*");
    return () => {
      window.removeEventListener("message", receive);
      for (const pending of pendingTokenRequests.current.values()) {
        window.clearTimeout(pending.timer);
        pending.resolve({ token: null, error: "auth_unavailable" });
      }
      pendingTokenRequests.current.clear();
    };
  }, []);

  const openAuthSetup = useCallback(() => {
    targetWindow.current?.postMessage({
      type: OPEN_AUTH_SETUP_MESSAGE,
      channel: channel.current,
    }, parentOrigin.current);
  }, []);

  useEffect(() => {
    if (authState === "ready") return undefined;
    const verifyWhenVisible = () => {
      if (document.visibilityState === "visible") checkAuthentication();
    };
    document.addEventListener("visibilitychange", verifyWhenVisible);
    return () => document.removeEventListener(
      "visibilitychange",
      verifyWhenVisible,
    );
  }, [authState, checkAuthentication]);

  useEffect(() => {
    if (pageContext) checkAuthentication();
  }, [checkAuthentication, pageContext]);

  const appContext = useMemo(() => pageContext ? ({
    route: pageContext.url,
    interfaceHelp: false,
    activeEntity: pageContext.cuij ? {
      type: "expediente",
      id: pageContext.cuij,
      label: `Expediente ${pageContext.cuij}`,
    } : null,
    pageState: {
      documentOpen: true,
      documentType: pageContext.kind,
      source: pageContext.source,
      sourceId: pageContext.expId || pageContext.url,
      sourceUrl: pageContext.url,
      fileName: pageContext.title,
      capturedAt: pageContext.capturedAt,
      cuij: pageContext.cuij,
      expandedCount: pageContext.expandedCount,
      expandedExpedientes: pageContext.expandedExpedientes,
      expId: pageContext.expId,
      actId: pageContext.actId,
      text: pageContext.text,
    },
  }) : null, [pageContext]);

  if (!appContext) {
    return (
      <main className="extension-widget-waiting" role="status">
        <img src="/favicon.svg" alt="" />
        <p>Preparando el contexto de la página…</p>
      </main>
    );
  }

  if (authState !== "ready") {
    return (
      <ExtensionWidgetSignIn
        authError={authError}
        checking={authState === "checking"}
        onConfigure={openAuthSetup}
        onRetry={checkAuthentication}
      />
    );
  }

  return (
    <main className="extension-widget-shell">
      <Justiniano
        variant="page"
        appId="praxis"
        appName="Praxis"
        apiUrl={import.meta.env.VITE_API_URL || "http://localhost:4000"}
        getAccessToken={widgetAccessToken}
        welcome="Puedo ayudarte a entender lo que estás viendo en el Poder Judicial de Mendoza y relacionarlo con tu expediente."
        suggestions={[
          "Resumí lo que estoy viendo",
          "¿Qué debería revisar con atención?",
          "Explicame esta actuación en términos simples",
        ]}
        appContext={appContext}
        onRequestClose={() => {
          targetWindow.current?.postMessage({
            type: CLOSE_MESSAGE,
            channel: channel.current,
          }, parentOrigin.current);
        }}
      />
    </main>
  );
}
